import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Canonical tenant/subdomain detection — SINGLE source of truth.
 *
 * Priority:
 *   1. ?tenant= query param (works everywhere: dev, preview, prod, localhost)
 *   2. *.localhost subdomain (e.g. adara.localhost → "adara")
 *   3. *.karpung.com / *.karpung.dev / *.pages.dev subdomain
 *      (reserved names api|www|cdn|img are filtered out)
 *   4. Bare .pages.dev / .karpung.dev preview URL (e.g. karpung.pages.dev)
 *   5. Generic custom domain: first label before the first dot
 *   6. Fallback to `defaultTenant`
 *
 * @param defaultTenant — used when running SSR (no window), on plain
 *   localhost/127.0.0.1, or when no tenant can be determined otherwise.
 */
export function detectTenant(defaultTenant = "karpung"): string {
  // SSR / server-side: no window available
  if (typeof window === "undefined") return defaultTenant;

  // 1. Explicit ?tenant= query param — highest priority, works everywhere
  const url = new URL(window.location.href);
  const queryTenant = url.searchParams.get("tenant");
  if (queryTenant) return queryTenant;

  const host = window.location.hostname;
  if (host === "smkn3kabtangerang.sch.id" || host === "www.smkn3kabtangerang.sch.id") return "smkn3";

  // 2. *.localhost subdomain — dev mode with explicit subdomain
  const localMatch = host.match(/^([a-z0-9-]+)\.localhost/);
  if (localMatch) return localMatch[1];

  // 3. Plain localhost / 127.0.0.1 — fallback to configured default
  if (host === "localhost" || host === "127.0.0.1") return defaultTenant;

  // 4. Known deployment / custom domains
  const knownMatch = host.match(
    /^([a-z0-9-]+)\.(?:karpung\.com|karpung\.dev|pages\.dev)/,
  );
  if (
    knownMatch &&
    !["api", "www", "cdn", "img"].includes(knownMatch[1])
  ) {
    return knownMatch[1];
  }

  // 5. Preview deploys at root of *.pages.dev or *.karpung.dev
  //    (no tenant-specific subdomain — e.g. karpung.pages.dev)
  if (host.endsWith(".pages.dev") || host.endsWith(".karpung.dev")) {
    return defaultTenant;
  }

  // 6. Generic custom domain: grab the first label
  const genericMatch = host.match(/^([a-z0-9-]+)\./);
  return genericMatch ? genericMatch[1] : defaultTenant;
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
